import React from 'react';
import {
  ArrowRight,
  KeyRound,
  MailCheck,
  ShieldCheck,
} from 'lucide-react';
import { useLocation, useNavigate } from 'react-router-dom';

import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { AuthShell } from './auth-shell';
import {
  FORGOT_PASSWORD_PATH,
  LOGIN_PATH,
  resolveAuthenticatedPath,
} from './auth-routing';
import { useAuth } from './auth-context';

interface CreateAccountLocationState {
  email?: string;
}

const accessHighlights = [
  {
    icon: MailCheck,
    title: 'Use o email da sua conta',
    description:
      'Com o email certo, pode entrar ou recuperar o acesso num instante.',
  },
  {
    icon: KeyRound,
    title: 'Recupere a password quando precisar',
    description:
      'Se não se lembra da password, enviamos um link para voltar a entrar com segurança.',
  },
  {
    icon: ShieldCheck,
    title: 'Acesso acompanhado pela sua equipa',
    description:
      'Se ainda não recebeu credenciais, quem gere a sua conta pode ajudar a ativá-la.',
  },
] as const;

const accessMetrics = [
  { value: '1 email', label: 'Para identificar a conta' },
  { value: '2 caminhos', label: 'Entrar ou recuperar' },
  { value: 'Ajuda rápida', label: 'Se ainda não tem acesso' },
] as const;

const fieldClassName =
  'h-14 rounded-[1.35rem] border-[#dfd4c6] bg-[#fffdf9] text-[#16323b] shadow-[0_14px_28px_rgba(50,40,27,0.08)] placeholder:text-[#8c8578] focus-visible:ring-[#0f766e]/25 focus-visible:ring-offset-[#fffaf4]';

const labelClassName =
  'block text-left text-sm font-semibold uppercase tracking-[0.18em] text-[#6d665c]';

export function CreateAccountPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { isAuthenticated, session } = useAuth();
  const locationState =
    location.state && typeof location.state === 'object'
      ? (location.state as CreateAccountLocationState)
      : undefined;
  const currentEmail = locationState?.email ?? '';

  React.useEffect(() => {
    if (isAuthenticated && session) {
      navigate(resolveAuthenticatedPath(session), { replace: true });
    }
  }, [isAuthenticated, navigate, session]);

  return (
    <AuthShell
      heroBadge="Acesso à conta"
      heroTitle="Entre ou recupere o acesso à sua conta."
      heroDescription="Se já recebeu acesso, entre com o seu email ou redefina a password. Se ainda não recebeu, confirme com quem gere a sua conta."
      heroHighlights={accessHighlights}
      heroMetrics={accessMetrics}
      formBadge="Acesso"
      formTitle="Como continuar"
      formDescription="Use o email da conta para entrar ou iniciar a recuperação da password."
      footer={
        <div className="text-center text-sm font-medium leading-6 text-[#6b736d]">
          Se ainda não tiver acesso, fale com quem gere a sua conta para o ativar.
        </div>
      }
    >
      <div className="space-y-6">
        <div className="rounded-[1.5rem] border border-[#cae7dc] bg-[#ebf7f1] px-4 py-4 text-sm font-medium leading-6 text-[#0f766e]">
          Esta página já não cria contas novas. O email abaixo continua a ser
          usado para entrar ou recuperar o acesso.
        </div>

        <label className="space-y-3" htmlFor="access-email">
          <span className={labelClassName}>Email para o acesso</span>
          <Input
            id="access-email"
            type="email"
            value={currentEmail}
            readOnly
            placeholder="seu@email.com"
            className={fieldClassName}
          />
        </label>

        <div className="rounded-[1.5rem] border border-[#efe4d7] bg-[#fff7ef]/80 p-5 text-sm leading-6 text-[#5f655f]">
          Se já tem conta, entre diretamente. Se precisar, pode pedir um link
          para definir uma nova password.
        </div>

        <div className="grid gap-3 sm:grid-cols-2">
          <Button
            type="button"
            size="lg"
            className="rounded-[1.35rem] bg-[#0f766e] text-base shadow-[0_20px_40px_rgba(15,118,110,0.24)] hover:bg-[#0c615b]"
            onClick={() =>
              navigate(LOGIN_PATH, {
                replace: true,
                state: { email: currentEmail },
              })
            }
          >
            Entrar com este email
            <ArrowRight className="ml-2 h-5 w-5" />
          </Button>

          <Button
            type="button"
            variant="ghost"
            size="lg"
            className="rounded-[1.35rem] border border-[#e5d8ca] bg-[#fff7ef] text-[1.02rem] text-[#16323b] hover:bg-[#f8efe3]"
            onClick={() =>
              navigate(FORGOT_PASSWORD_PATH, {
                state: { email: currentEmail },
              })
            }
          >
            Recuperar password
          </Button>
        </div>
      </div>
    </AuthShell>
  );
}
